import { tracker } from '@lessonup/analytics';
import { useDocumentMutation } from '@lessonup/client-integration';
import { ManagedModal, NiceModal, NiceModalHocProps } from '@lessonup/ui-components';
import React, { useEffect, useState } from 'react';
import { useFeatureFlags } from '../../../../../../utils/featureFlags/useFeatureFlags';
import { useEditorTranslation } from '../../../../hooks/useEditorTranslation';
import { ShareLessonWithStudentsDocument } from './ShareLessonModal.graphql.generated';
import { ShareLessonModalInnerProps } from './ShareLessonModal.types';
import { ShareLessonModalInner } from './ShareLessonModalInner';

export interface ShareLessonModalProps
  extends Pick<ShareLessonModalInnerProps, 'lessonId' | 'exportToPdfDirect' | 'lessonShareUrl'> {
  source: 'editor' | 'explorer';
  getShareLessonWithStudents?: (shareId: string) => string;
}

export const ShareLessonModal: React.FC<ShareLessonModalProps & NiceModalHocProps> = NiceModal.create(
  ({ lessonShareUrl, lessonId, exportToPdfDirect, getShareLessonWithStudents, source }: ShareLessonModalProps) => {
    const { t } = useEditorTranslation();
    const [shareLessonWithStudentsUrl, setShareLessonWithStudentsUrl] = useState<string>('');
    const [shareLessonWithStudentsError, setShareLessonWithStudentsError] = useState<Error | undefined>(undefined);
    const { isFeatureEnabled } = useFeatureFlags();
    const [getShareLessonWithStudentsShareId, { loading }] = useDocumentMutation(ShareLessonWithStudentsDocument);

    useEffect(() => {
      let cancelled = false;

      async function shareLessonsWithStudents() {
        if (!isFeatureEnabled('shareLessonWithStudents') || !getShareLessonWithStudents || cancelled) return;

        const result = await getShareLessonWithStudentsShareId({
          variables: { lessonId },
          onError: (error: Error) => !cancelled && setShareLessonWithStudentsError(error),
        });

        if (cancelled) return;

        if (!result.data?.shareLessonWithStudents) {
          setShareLessonWithStudentsUrl('');
          setShareLessonWithStudentsError(new Error('Link not generated'));
          return;
        }

        const url = getShareLessonWithStudents(result.data?.shareLessonWithStudents);

        setShareLessonWithStudentsUrl(url);
        setShareLessonWithStudentsError(undefined);
      }

      if (!cancelled) shareLessonsWithStudents();

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const modal = NiceModal.useModal();

    const isSharedWithStudentsEnabled = isFeatureEnabled('shareLessonWithStudents');

    const handleModalClose = () => {
      modal.resolve(undefined);
      modal.hide();
    };

    function handlePdfDownloadSuccess() {
      tracker.events.lessonExport({ editorVersion: 'v2', context: contextForSource(source), lessonId });
      handleModalClose();
    }

    return (
      <ManagedModal modal={modal} contentLabel={t('shareLessonModal.modalContentLabel')} maxWidth="35rem">
        <ShareLessonModalInner
          handleModalClose={handleModalClose}
          lessonId={lessonId}
          loading={loading}
          isShareWithStudentsEnabled={isSharedWithStudentsEnabled}
          lessonShareUrl={lessonShareUrl}
          onCopyTeacherShareUrl={() => {
            tracker.events.lessonShareWithTeacher({
              lessonId,
              lessonShareMethod: 'copy_link',
              context: contextForSource(source),
            });
          }}
          onCopyStudentShareUrl={() => {
            tracker.events.lessonShareWithStudent({
              lessonId,
              lessonShareMethod: 'copy_link',
              context: contextForSource(source),
            });
          }}
          onPdfDownloadSuccess={handlePdfDownloadSuccess}
          shareLessonWithStudentsUrl={shareLessonWithStudentsUrl}
          shareLessonsWithStudentsError={shareLessonWithStudentsError}
          exportToPdfDirect={exportToPdfDirect}
        />
      </ManagedModal>
    );
  }
);

export const showShareLessonModal = (props: ShareLessonModalProps): Promise<void> => {
  return NiceModal.show(ShareLessonModal, props);
};

function contextForSource(source: ShareLessonModalProps['source']) {
  return source === 'explorer' ? 'explorer_share_modal' : 'editor_share_modal';
}
